// 시험 결과 화면의 학생별 입실·퇴실 — 성취도 API `GET /exam-answers/exam/{exam_id}/connections` 응답을
// 결과(`students[]`, 제출자만)·응시자 명단(플랫폼 API)과 이메일로 합친다.
// 입실 = 학생 채널에 처음 연결한 시각, 퇴실 = 제출 뒤 첫 끊김(미제출이면 마지막 끊김), 이탈 = 제출 전 끊긴 횟수.
// 시각은 모두 UTC(타임존 표기 없음). 계약: docs/시험_답안_채점_API.md 9-1절
import { parseUtc, formatUtcToTimeZone } from '@/utils/datetime'

// 이메일 정규화 (대소문자/공백 차이 흡수) — ExamMonitor 와 같은 규칙
export const normalizeEmail = (email) => (email || '').trim().toLowerCase()

// connections 응답의 students[] → { [정규화 이메일]: { enteredAt, exitedAt, leaveCount, submittedAt } }
export const indexConnections = (students) => {
  const map = {}
  ;(Array.isArray(students) ? students : []).forEach((c) => {
    const key = normalizeEmail(c?.learner_email)
    if (!key) return
    map[key] = {
      enteredAt: c.entered_at || null,
      exitedAt: c.exited_at || null,
      leaveCount: Number(c.leave_count) || 0,
      submittedAt: c.submitted_at || null
    }
  })
  return map
}

// 소요 시간(초) = 제출 − 입실. 둘 다 UTC. 하나라도 없거나 제출이 입실보다 앞서면 null
export const elapsedSeconds = (enteredAt, submittedAt) => {
  const start = parseUtc(enteredAt)
  const end = parseUtc(submittedAt)
  if (!start || !end) return null
  const seconds = Math.round((end.getTime() - start.getTime()) / 1000)
  return seconds < 0 ? null : seconds
}

// UTC 시각 → 시험 타임존의 'HH:mm' (withDate 면 'MM-DD HH:mm'). 값이 없으면 '-'
export const formatClock = (utcValue, timeZone, { withDate = false } = {}) => {
  const local = formatUtcToTimeZone(utcValue, timeZone || 'Asia/Seoul') // YYYY-MM-DDTHH:MM:SS
  if (!local) return '-'
  const time = local.slice(11, 16)
  return withDate ? `${local.slice(5, 10)} ${time}` : time
}

// 제출 시각(UTC) 기준 정렬 비교 — 값이 없는 행은 뒤로
export const compareSubmittedAt = (a, b) => {
  const ta = parseUtc(a)?.getTime() ?? Infinity
  const tb = parseUtc(b)?.getTime() ?? Infinity
  if (ta === tb) return 0
  return ta < tb ? -1 : 1
}

// 이름 비교 — 「이름순」 정렬과 미제출 묶음이 같이 쓴다. 이름이 없으면 빈 문자열로 본다.
// 플랫폼 백엔드의 수강생 목록(클래스 상세 enrollments[]·학습활동 users[])과 같은 ICU 기본 정렬이다:
// 기호 → 숫자 → 영문 → 한글 → 가나 → 한자, 영문은 대소문자와 관계없이 알파벳순, 한글은 가나다순 (docs/학습_API_명세.md 1.3)
// 'en' 은 ICU 기본 정렬 그대로다. 로캘을 비우거나 'und' 로 두면 브라우저 언어를 따라 한국어 브라우저에서 한글이 먼저 온다
const nameCollator = new Intl.Collator('en')
export const compareName = (a, b) => nameCollator.compare(a || '', b || '')

// 제출하지 않은 학생 행 — 응시자 명단에서 제출자를 뺀 학생 + 명단에는 없지만 연결 기록이 있는 학생.
// 연결 기록이 있으면 입실·퇴실·이탈을 붙인다. 입실한 학생을 입실 순으로 먼저, 기록 없는 학생은 이름순.
export const buildUnsubmitted = (roster, submittedEmails, connections) => {
  const submitted = new Set((submittedEmails || []).map(normalizeEmail).filter(Boolean))
  const seen = new Set()
  const rows = []
  const push = (email, name, key) => {
    const conn = (connections && connections[key]) || {}
    rows.push({
      studentId: `unsubmitted:${key}`,
      studentName: name || email,
      studentEmail: email,
      status: 'unsubmitted',
      enteredAt: conn.enteredAt || null,
      exitedAt: conn.exitedAt || null,
      leaveCount: conn.leaveCount || 0
    })
  }

  ;(Array.isArray(roster) ? roster : []).forEach((s) => {
    const email = s?.userEmail || s?.email || ''
    const key = normalizeEmail(email)
    if (!key || submitted.has(key) || seen.has(key)) return
    seen.add(key)
    push(email, s.userName || s.name, key)
  })
  Object.keys(connections || {}).forEach((key) => {
    if (submitted.has(key) || seen.has(key)) return
    seen.add(key)
    push(key, null, key)
  })

  rows.sort((a, b) => {
    const byEntered = compareSubmittedAt(a.enteredAt, b.enteredAt)
    if (byEntered !== 0) return byEntered
    return compareName(a.studentName, b.studentName)
  })
  return rows
}
